import { useState, useCallback } from 'react';
import { DrillDownData } from '../types/productDrillDown';

interface UseProductDrillDownReturn {
  isModalOpen: boolean;
  drillDownData: DrillDownData | null;
  loading: boolean;
  selectedProductName: string;
  openModal: (productName: string, codItem: string, spedFiscalId: number, indOper?: string) => Promise<void>;
  closeModal: () => void;
  error: string | null;
}

export const useProductDrillDown = (): UseProductDrillDownReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [drillDownData, setDrillDownData] = useState<DrillDownData | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const openModal = useCallback(async (productName: string, codItem: string, spedFiscalId: number, indOper?: string) => {
    setSelectedProductName(productName);
    setIsModalOpen(true);
    setLoading(true);
    setError(null);

    try {
      const url = new URL(`${process.env.REACT_APP_API_URL}produto-detalhes-drill-down/${spedFiscalId}/${codItem}`);
      if (indOper) {
        url.searchParams.set('ind_oper', indOper);
      }
      
      const response = await fetch(url.toString());
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const detailData: DrillDownData = await response.json();
      
      if (detailData && typeof detailData === 'object') {
        // Processar dados para garantir retrocompatibilidade
        const processedData = {
          ...detailData,
          transacoes_por_mes: detailData.transacoes_por_mes?.map(transacao => ({
            ...transacao,
            // Calcular total_transacoes se não estiver presente mas os novos campos estiverem
            total_transacoes: transacao.total_transacoes || 
              ((transacao.total_transacoes_credito || 0) + (transacao.total_transacoes_debito || 0)) || 0
          }))
        };
        
        setDrillDownData(processedData);
      } else {
        console.error('Estrutura de dados inválida:', detailData);
        setDrillDownData(null);
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do produto:', error);
      setDrillDownData(null);
      setError(error instanceof Error ? error.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setDrillDownData(null);
    setSelectedProductName('');
  }, []);

  return {
    isModalOpen,
    drillDownData,
    loading,
    selectedProductName,
    openModal,
    closeModal,
    error,
  };
}; 